import { keyboardFocusRing } from '@mangovoice/design-system/recipes/focusRing'
import { FontWeight, textStyleKeys } from '@mangovoice/design-system/textStyles'

import { tokenVar } from '../../utils/designTokens'
import { Radii, Size } from '../types'
import { createMultiStyleConfigHelpers } from './styleHelpers'

const mangoButtonParts = ['button', 'text', 'nodeContainer', 'icon'] as const

const helpers = createMultiStyleConfigHelpers(mangoButtonParts)

export enum MangoButtonVariant {
  Default_100 = 'default_100',
  Default_200 = 'default_200',
  Default_300 = 'default_300',
  Destructive_100 = 'destructive_100',
  Destructive_200 = 'destructive_200',
  Destructive_300 = 'destructive_300',
  Success_100 = 'success_100',
  Success_200 = 'success_200',
  Success_300 = 'success_300',
  Brand = 'brand',
  Brand_Text = 'brand_text',
  Neutral_100 = 'neutral_100',
  Neutral_200 = 'neutral_200',
  Neutral_300 = 'neutral_300',
  Login = 'login',
}

export const MangoButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    button: {
      display: 'flex',
      gap: '0.5rem',
      flexShrink: 0,
      borderRadius: Radii.lg,
      border: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      _disabled: { cursor: 'not-allowed', opacity: 1, userSelect: 'none' },
      ...keyboardFocusRing,
    },
    nodeContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    text: { textStyle: textStyleKeys.Body2, margin: 0 },
  },
  sizes: {
    [Size.md]: {
      button: { px: '1.25rem', height: '2.5rem' },
      icon: { boxSize: '1.25rem' },
    },
    [Size.sm]: {
      button: { px: '0.75rem', height: '1.75rem' },
      icon: { boxSize: '1rem' },
    },
  },
  variants: {
    [MangoButtonVariant.Brand]: {
      button: {
        backgroundColor: tokenVar('brand.solid'),
        _hover: {
          backgroundColor: tokenVar('brand.emphasized'),
          boxShadow: tokenVar('shadows.default'),
        },
        _active: {
          backgroundColor: tokenVar('brand.emphasized'),
          boxShadow: tokenVar('shadows.default'),
        },
        _disabled: {
          backgroundColor: tokenVar('bg.muted'),
          _hover: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('brand.contrast'),
        _groupHover: { color: tokenVar('brand.contrast') },
        _groupActive: { color: tokenVar('brand.contrast') },
        _groupDisabled: { color: tokenVar('brand.contrast') },
      },
      icon: {
        color: tokenVar('brand.contrast'),
        _groupHover: { color: tokenVar('brand.contrast') },
        _groupActive: { color: tokenVar('brand.contrast') },
        _groupDisabled: { color: tokenVar('brand.contrast') },
      },
    },
    [MangoButtonVariant.Brand_Text]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: 'transparent' },
        _active: { backgroundColor: 'transparent' },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: { backgroundColor: 'transparent' },
          _active: { backgroundColor: 'transparent' },
        },
      },
      text: {
        color: tokenVar('brand.fg'),
        _groupHover: { color: tokenVar('brand.fg') },
        _groupActive: { color: tokenVar('brand.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('brand.fg'),
        _groupHover: { color: tokenVar('brand.fg') },
        _groupActive: { color: tokenVar('brand.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Default_300]: {
      button: {
        backgroundColor: tokenVar('brand.solid'),
        _hover: {
          backgroundColor: tokenVar('brand.emphasized'),
          boxShadow: tokenVar('shadows.focus'),
        },
        _active: {
          backgroundColor: tokenVar('brand.emphasized'),
          boxShadow: tokenVar('shadows.focus'),
        },
        _disabled: {
          backgroundColor: tokenVar('bg.emphasized'),
          _hover: {
            backgroundColor: tokenVar('bg.emphasized'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.emphasized'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: tokenVar('brand.contrast'),
        _groupHover: { color: tokenVar('brand.contrast') },
        _groupActive: { color: tokenVar('brand.contrast') },
        _groupDisabled: { color: tokenVar('brand.contrast') },
      },
      icon: {
        color: tokenVar('brand.contrast'),
        _groupHover: { color: tokenVar('brand.contrast') },
        _groupActive: { color: tokenVar('brand.contrast') },
        _groupDisabled: { color: tokenVar('brand.contrast') },
      },
    },
    [MangoButtonVariant.Default_200]: {
      button: {
        backgroundColor: tokenVar('bg.muted'),
        _hover: { backgroundColor: tokenVar('bg.emphasized') },
        _active: { backgroundColor: tokenVar('bg.emphasized') },
        _disabled: {
          backgroundColor: tokenVar('bg.subtle'),
          _hover: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        fontWeight: FontWeight.Normal,
        fontSize: '14px',
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        fontSize: '14px',
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Default_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: tokenVar('bg.muted') },
        _active: { backgroundColor: tokenVar('bg.muted') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Destructive_300]: {
      button: {
        backgroundColor: tokenVar('red.solid'),
        _hover: {
          backgroundColor: tokenVar('red.emphasized'),
          boxShadow: tokenVar('shadows.error'),
        },
        _active: {
          backgroundColor: tokenVar('red.solid'),
          boxShadow: tokenVar('shadows.error'),
        },
        _disabled: {
          backgroundColor: tokenVar('bg.emphasized'),
          _hover: {
            backgroundColor: tokenVar('bg.emphasized'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.emphasized'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: tokenVar('red.contrast'),
        _groupHover: { color: tokenVar('red.contrast') },
        _groupActive: { color: tokenVar('red.contrast') },
        _groupDisabled: { color: tokenVar('red.contrast') },
      },
      icon: {
        color: tokenVar('red.contrast'),
        _groupHover: { color: tokenVar('red.contrast') },
        _groupActive: { color: tokenVar('red.contrast') },
        _groupDisabled: { color: tokenVar('red.contrast') },
      },
    },
    [MangoButtonVariant.Destructive_200]: {
      button: {
        backgroundColor: tokenVar('red.subtle'),
        _hover: { backgroundColor: tokenVar('red.muted') },
        _active: { backgroundColor: tokenVar('red.muted') },
        _disabled: {
          backgroundColor: tokenVar('bg.subtle'),
          _hover: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('red.fg'),
        _groupHover: { color: tokenVar('red.fg') },
        _groupActive: { color: tokenVar('red.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('red.fg'),
        _groupHover: { color: tokenVar('red.fg') },
        _groupActive: { color: tokenVar('red.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Destructive_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: tokenVar('red.muted') },
        _active: { backgroundColor: tokenVar('red.muted') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('red.fg'),
        _groupHover: { color: tokenVar('red.fg') },
        _groupActive: { color: tokenVar('red.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('red.fg'),
        _groupHover: { color: tokenVar('red.fg') },
        _groupActive: { color: tokenVar('red.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Success_300]: {
      button: {
        backgroundColor: tokenVar('green.solid'),
        _hover: {
          backgroundColor: tokenVar('green.emphasized'),
          boxShadow: tokenVar('shadows.success'),
        },
        _active: {
          backgroundColor: tokenVar('green.solid'),
          boxShadow: tokenVar('shadows.success'),
        },
        _disabled: {
          backgroundColor: tokenVar('bg.muted'),
          _hover: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: tokenVar('green.contrast'),
        _groupHover: { color: tokenVar('green.contrast') },
        _groupActive: { color: tokenVar('green.contrast') },
        _groupDisabled: { color: tokenVar('green.contrast') },
      },
      icon: {
        color: tokenVar('green.contrast'),
        _groupHover: { color: tokenVar('green.contrast') },
        _groupActive: { color: tokenVar('green.contrast') },
        _groupDisabled: { color: tokenVar('green.contrast') },
      },
    },
    [MangoButtonVariant.Success_200]: {
      button: {
        backgroundColor: tokenVar('bg.subtle'),
        _hover: { backgroundColor: tokenVar('green.subtle') },
        _active: { backgroundColor: tokenVar('green.muted') },
        _disabled: {
          backgroundColor: tokenVar('bg.subtle'),
          _hover: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('green.fg') },
        _groupActive: { color: tokenVar('green.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('green.fg') },
        _groupActive: { color: tokenVar('green.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Success_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: tokenVar('bg.muted') },
        _active: { backgroundColor: tokenVar('bg.emphasized') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: tokenVar('bg.muted') },
        _active: { backgroundColor: tokenVar('bg.emphasized') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_200]: {
      button: {
        backgroundColor: tokenVar('bg.default'),
        _hover: { backgroundColor: tokenVar('bg.emphasized') },
        _active: { backgroundColor: tokenVar('bg.muted') },
        _disabled: {
          backgroundColor: tokenVar('bg.subtle'),
          _hover: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('fg.default') },
        _groupActive: { color: tokenVar('fg.default') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_300]: {
      button: {
        backgroundColor: tokenVar('bg.default'),
        _hover: {
          backgroundColor: tokenVar('bg.emphasized'),
          boxShadow: tokenVar('shadows.default'),
        },
        _active: {
          backgroundColor: tokenVar('bg.emphasized'),
          boxShadow: `0 0.25rem 0.75rem 0 ${tokenVar('bg.muted')}`,
        },
        _disabled: {
          backgroundColor: tokenVar('bg.muted'),
          _hover: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: tokenVar('fg.muted'),
        _groupHover: { color: tokenVar('fg.muted') },
        _groupActive: { color: tokenVar('fg.muted') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.muted'),
        _groupHover: { color: tokenVar('fg.muted') },
        _groupActive: { color: tokenVar('fg.muted') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
    [MangoButtonVariant.Login]: {
      button: {
        backgroundColor: tokenVar('bg.subtle'),
        _hover: { backgroundColor: tokenVar('brand.subtle') },
        _active: { backgroundColor: tokenVar('brand.muted') },
        _disabled: {
          backgroundColor: tokenVar('bg.subtle'),
          _hover: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: tokenVar('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('brand.fg') },
        _groupActive: { color: tokenVar('brand.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
      icon: {
        color: tokenVar('fg.default'),
        _groupHover: { color: tokenVar('brand.fg') },
        _groupActive: { color: tokenVar('brand.fg') },
        _groupDisabled: { color: tokenVar('fg.muted') },
      },
    },
  },
})
